"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
exports.testReset = testReset;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _react = _interopRequireDefault(require("react"));
var _Grow = _interopRequireDefault(require("../Grow"));
var _Popper = _interopRequireDefault(require("../Popper"));
var _tss = require("../styles/tss");
var _useTheme = require("../styles/useTheme");
var _capitalizeFirstWord = require("../utilities/capitalizeFirstWord");
var _focusVisible = require("../utilities/focusVisible");
var _unstable_useId = _interopRequireDefault(require("../utilities/unstable_useId"));
var _useControlled = _interopRequireDefault(require("../utilities/useControlled"));
var _useEventCallback = _interopRequireDefault(require("../utilities/useEventCallback"));
var _useForkRef = _interopRequireDefault(require("../utilities/useForkRef"));
const _excluded = ["textTransform"],
  _excluded2 = ["arrow", "children", "classes", "describeChild", "disableFocusListener", "disableHoverListener", "disableTouchListener", "enterDelay", "enterNextDelay", "enterTouchDelay", "followCursor", "id", "disableInteractive", "leaveDelay", "leaveTouchDelay", "onClose", "onOpen", "open", "placement", "PopperComponent", "PopperProps", "title", "TransitionComponent", "TransitionProps"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { (0, _defineProperty2.default)(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function round(value) {
  return Math.round(value * 1e5) / 1e5;
}
const ARROW_WIDTH = '10px';
const ARROW_HEIGHT = '7px'; // = width / sqrt(2) = (length of the hypotenuse)

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function arrowGenerator(classes) {
  return {
    [`&[data-popper-placement*="bottom"] .${classes.arrow}`]: {
      top: 0,
      left: 0,
      marginTop: `-${ARROW_HEIGHT}`,
      '&::before': {
        transformOrigin: '0 100%'
      }
    },
    [`&[data-popper-placement*="top"] .${classes.arrow}`]: {
      bottom: 0,
      left: 0,
      marginBottom: `-${ARROW_HEIGHT}`,
      '&::before': {
        transformOrigin: '100% 0'
      }
    },
    [`&[data-popper-placement*="right"] .${classes.arrow}`]: {
      left: 0,
      marginLeft: `-${ARROW_HEIGHT}`,
      height: ARROW_WIDTH,
      width: ARROW_HEIGHT,
      '&::before': {
        transformOrigin: '100% 100%'
      }
    },
    [`&[data-popper-placement*="left"] .${classes.arrow}`]: {
      right: 0,
      marginRight: `-${ARROW_HEIGHT}`,
      height: ARROW_WIDTH,
      width: ARROW_HEIGHT,
      '&::before': {
        transformOrigin: '0 0'
      }
    }
  };
}
const useStyles = (0, _tss.makeTssStyles)({
  name: 'DsTooltip',
  uniqId: '5DLxF6'
})((theme, _params, classes) => {
  const {
    colors,
    spacing,
    typography
  } = theme.busbud;
  const spacingBetweenElements = spacing(0.5);
  const _typography$font$comp = typography.font.component.label.sm,
    {
      textTransform: textTransformSm
    } = _typography$font$comp,
    labelSm = (0, _objectWithoutProperties2.default)(_typography$font$comp, _excluded);

  // arrow real height is 5px but gets 7px allocated = 2px margin
  // we want a 4px margin --> we need 2 extra px margin
  const arrowMargin = `calc(${ARROW_HEIGHT} + ${spacing(0.25)})`;
  return {
    /* Styles applied to the Popper component. */
    popper: {
      zIndex: theme.zIndex.tooltip,
      pointerEvents: 'none' // disable jss-rtl plugin
    },
    /* Styles applied to the Popper component if `arrow={true}`. */
    popperArrow: arrowGenerator(classes),
    /* Styles applied to the tooltip (label wrapper) element. */
    tooltip: _objectSpread(_objectSpread({}, labelSm), {}, {
      // Error on casting string to a valid textTransform value
      // TODO remove when legacy theme is removed
      textTransform: textTransformSm,
      backgroundColor: colors.inverseBackground.primary.opaque,
      borderRadius: theme.shape.borderRadius,
      color: colors.text.overBackground.opaque,
      fontFamily: theme.typography.fontFamily,
      padding: spacing(0.5, 1),
      maxWidth: 300,
      wordWrap: 'break-word',
      fontWeight: theme.typography.fontWeightBold
    }),
    /* Styles applied to the tooltip (label wrapper) element if `arrow={true}`. */
    tooltipArrow: {
      position: 'relative',
      margin: '0'
    },
    /* Styles applied to the arrow element. */
    arrow: {
      overflow: 'hidden',
      position: 'absolute',
      width: ARROW_WIDTH,
      height: ARROW_HEIGHT,
      boxSizing: 'border-box',
      color: colors.inverseBackground.primary.opaque,
      '&::before': {
        content: '""',
        margin: 'auto',
        display: 'block',
        width: '100%',
        height: '100%',
        backgroundColor: 'currentColor',
        transform: 'rotate(45deg)'
      }
    },
    /* Styles applied to the tooltip (label wrapper) element if the tooltip is opened by touch. */
    touch: {
      padding: spacing(1, 2),
      fontSize: theme.typography.pxToRem(14),
      lineHeight: `${round(16 / 14)}em`,
      fontWeight: theme.typography.fontWeightRegular
    },
    /* Styles applied to the tooltip (label wrapper) element if `placement` contains "left". */
    tooltipPlacementLeft: {
      transformOrigin: 'right center',
      marginRight: spacing(0.5),
      [`&.${classes.tooltipArrow}`]: {
        marginRight: arrowMargin
      },
      [theme.breakpoints.up('sm')]: {
        marginRight: spacingBetweenElements
      }
    },
    /* Styles applied to the tooltip (label wrapper) element if `placement` contains "right". */
    tooltipPlacementRight: {
      transformOrigin: 'left center',
      marginLeft: spacing(0.5),
      [`&.${classes.tooltipArrow}`]: {
        marginLeft: arrowMargin
      },
      [theme.breakpoints.up('sm')]: {
        marginLeft: spacingBetweenElements
      }
    },
    /* Styles applied to the tooltip (label wrapper) element if `placement` contains "top". */
    tooltipPlacementTop: {
      transformOrigin: 'center bottom',
      marginBottom: spacing(0.5),
      [`&.${classes.tooltipArrow}`]: {
        marginBottom: arrowMargin
      },
      [theme.breakpoints.up('sm')]: {
        marginBottom: spacingBetweenElements
      }
    },
    /* Styles applied to the tooltip (label wrapper) element if `placement` contains "bottom". */
    tooltipPlacementBottom: {
      transformOrigin: 'center top',
      marginTop: spacing(0.5),
      [`&.${classes.tooltipArrow}`]: {
        marginTop: arrowMargin
      },
      [theme.breakpoints.up('sm')]: {
        marginTop: spacingBetweenElements
      }
    }
  };
});
let hystersisOpen = false;
let hystersisTimer;
function testReset() {
  hystersisOpen = false;
  clearTimeout(hystersisTimer);
}
function composeEventHandler(handler, eventHandler) {
  return event => {
    if (eventHandler) {
      eventHandler(event);
    }
    handler(event);
  };
}
const getPlacementClassKey = placementInner => {
  const placement = placementInner.split('-')[0];
  return `tooltipPlacement${(0, _capitalizeFirstWord.capitalizeFirstWord)(placement)}`;
};
const Tooltip = /*#__PURE__*/_react.default.forwardRef(function Tooltip(props, ref) {
  const {
      arrow = false,
      children,
      classes: classesOverrides,
      describeChild = false,
      disableFocusListener = false,
      disableHoverListener = false,
      disableTouchListener = false,
      enterDelay = 100,
      enterNextDelay = 0,
      enterTouchDelay = 0,
      followCursor = false,
      id: idProp,
      disableInteractive: disableInteractiveProp = false,
      leaveDelay = 0,
      leaveTouchDelay = 1500,
      onClose,
      onOpen,
      open: openProp,
      placement = 'bottom',
      PopperComponent = _Popper.default,
      PopperProps,
      title,
      TransitionComponent = _Grow.default,
      TransitionProps
    } = props,
    other = (0, _objectWithoutProperties2.default)(props, _excluded2);
  const theme = (0, _useTheme.useTheme)();
  const {
    cx,
    classes
  } = useStyles(undefined, {
    props: {
      classes: classesOverrides
    }
  });
  const [childNode, setChildNode] = _react.default.useState();
  const [arrowRef, setArrowRef] = _react.default.useState(null);
  const ignoreNonTouchEvents = _react.default.useRef(false);
  const disableInteractive = disableInteractiveProp || followCursor;
  const closeTimer = _react.default.useRef();
  const enterTimer = _react.default.useRef();
  const leaveTimer = _react.default.useRef();
  const touchTimer = _react.default.useRef();
  const [openState, setOpenState] = (0, _useControlled.default)({
    controlled: openProp,
    default: false,
    name: 'Tooltip',
    state: 'open'
  });
  let open = openState;
  const id = (0, _unstable_useId.default)(idProp);
  _react.default.useEffect(() => {
    return () => {
      clearTimeout(closeTimer.current);
      clearTimeout(enterTimer.current);
      clearTimeout(leaveTimer.current);
      clearTimeout(touchTimer.current);
    };
  }, []);
  const handleOpen = event => {
    clearTimeout(hystersisTimer);
    hystersisOpen = true;

    // The mouseover event will trigger for every nested element in the tooltip.
    // We can skip rerendering when the tooltip is already open.
    // We are using the mouseover event instead of the mouseenter event to fix a hide/show issue.
    setOpenState(true);
    if (onOpen) {
      onOpen(event);
    }
  };
  const handleClose = (0, _useEventCallback.default)(event => {
    clearTimeout(hystersisTimer);
    hystersisTimer = setTimeout(() => {
      hystersisOpen = false;
    }, 800 + leaveDelay);
    setOpenState(false);
    if (onClose) {
      onClose(event);
    }
    clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => {
      ignoreNonTouchEvents.current = false;
    }, theme.transitions.duration.shortest);
  });
  const handleEnter = event => {
    if (ignoreNonTouchEvents.current && event.type !== 'touchstart') {
      return;
    }

    // Remove the title ahead of time.
    // We don't want to wait for the next render commit.
    // We would risk displaying two tooltips at the same time (native + this one).
    if (childNode) {
      // @ts-expect-error
      childNode.removeAttribute('title');
    }
    clearTimeout(enterTimer.current);
    clearTimeout(leaveTimer.current);
    if (enterDelay || hystersisOpen && enterNextDelay) {
      event.persist();
      enterTimer.current = window.setTimeout(() => {
        handleOpen(event);
      }, hystersisOpen ? enterNextDelay : enterDelay);
    } else {
      handleOpen(event);
    }
  };
  const handleLeave = event => {
    clearTimeout(enterTimer.current);
    clearTimeout(leaveTimer.current);
    event.persist();
    leaveTimer.current = window.setTimeout(() => {
      handleClose(event);
    }, leaveDelay);
  };
  const {
    isFocusVisibleRef,
    onBlurVisible: handleBlurVisible,
    onFocusVisible: handleFocusVisible,
    ref: focusVisibleRef
  } = (0, _focusVisible.useIsFocusVisible)();
  // We don't necessarily care about the focusVisible state (which is safe to access via ref anyway).
  // We just need to re-render the Tooltip if the focus-visible state changes.
  const [, setChildIsFocusVisible] = _react.default.useState(false);
  const handleBlur = event => {
    handleBlurVisible();
    if (isFocusVisibleRef.current === false) {
      setChildIsFocusVisible(false);
      handleLeave(event);
    }
  };
  const handleFocus = event => {
    // Workaround for https://github.com/facebook/react/issues/7769
    // The autoFocus of React might trigger the event before the componentDidMount.
    // We need to account for this eventuality.
    if (!childNode) {
      // @ts-expect-error
      setChildNode(event.currentTarget);
    }
    handleFocusVisible(event);
    if (isFocusVisibleRef.current === true) {
      setChildIsFocusVisible(true);
      handleEnter(event);
    }
  };
  const detectTouchStart = event => {
    ignoreNonTouchEvents.current = true;
    const childrenProps = children.props;
    if (childrenProps.onTouchStart) {
      childrenProps.onTouchStart(event);
    }
  };
  const handleMouseOver = handleEnter;
  const handleMouseLeave = handleLeave;
  const handleTouchStart = event => {
    detectTouchStart(event);
    clearTimeout(leaveTimer.current);
    clearTimeout(closeTimer.current);
    clearTimeout(touchTimer.current);
    event.persist();
    touchTimer.current = window.setTimeout(() => {
      handleEnter(event);
    }, enterTouchDelay);
  };
  const handleTouchEnd = event => {
    if (children.props.onTouchEnd) {
      children.props.onTouchEnd(event);
    }
    clearTimeout(touchTimer.current);
    clearTimeout(leaveTimer.current);
    event.persist();
    leaveTimer.current = window.setTimeout(() => {
      handleClose(event);
    }, leaveTouchDelay);
  };
  _react.default.useEffect(() => {
    if (!open) {
      return undefined;
    }
    function handleKeyDown(nativeEvent) {
      // IE11, Edge (prior to using Bink?) use 'Esc'
      if (nativeEvent.key === 'Escape' || nativeEvent.key === 'Esc') {
        handleClose(nativeEvent);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleClose, open]);
  const handleUseRef = (0, _useForkRef.default)(setChildNode, ref);
  const handleFocusRef = (0, _useForkRef.default)(focusVisibleRef, handleUseRef);
  // @ts-expect-error
  const handleRef = (0, _useForkRef.default)(children.ref, handleFocusRef);

  // There is no point in displaying an empty tooltip.
  if (title === '') {
    open = false;
  }
  const positionRef = _react.default.useRef({
    x: 0,
    y: 0
  });
  const popperRef = _react.default.useRef();
  const handleMouseMove = event => {
    const childrenProps = children.props;
    if (childrenProps.onMouseMove) {
      childrenProps.onMouseMove(event);
    }
    positionRef.current = {
      x: event.clientX,
      y: event.clientY
    };
    if (popperRef.current) {
      popperRef.current.update();
    }
  };
  const nameOrDescProps = {};
  function titleIsString(title) {
    return typeof title === 'string';
  }
  if (describeChild) {
    nameOrDescProps['title'] = !open && titleIsString(title) && !disableHoverListener ? title : null;
    nameOrDescProps['aria-describedby'] = open ? id : null;
  } else {
    nameOrDescProps['aria-label'] = titleIsString(title) ? title : null;
    nameOrDescProps['aria-labelledby'] = open && !titleIsString ? id : null;
  }
  const childrenProps = _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, nameOrDescProps), other), children.props), {}, {
    className: cx(other.className, children.props.className),
    onTouchStart: detectTouchStart,
    ref: handleRef
  }, followCursor ? {
    onMouseMove: handleMouseMove
  } : {});
  const interactiveWrapperListeners = {};
  if (!disableTouchListener) {
    childrenProps.onTouchStart = handleTouchStart;
    childrenProps.onTouchEnd = handleTouchEnd;
  }
  if (!disableHoverListener) {
    childrenProps.onMouseOver = composeEventHandler(
    // @ts-expect-error
    handleMouseOver, childrenProps.onMouseOver);
    childrenProps.onMouseLeave = composeEventHandler(
    // @ts-expect-error
    handleMouseLeave, childrenProps.onMouseLeave);
    if (!disableInteractive) {
      interactiveWrapperListeners.onMouseOver = handleMouseOver;
      interactiveWrapperListeners.onMouseLeave = handleMouseLeave;
    }
  }
  if (!disableFocusListener) {
    childrenProps.onFocus = composeEventHandler(
    // @ts-expect-error
    handleFocus, childrenProps.onFocus);
    childrenProps.onBlur = composeEventHandler(
    // @ts-expect-error
    handleBlur, childrenProps.onBlur);
    if (!disableInteractive) {
      interactiveWrapperListeners.onFocus = handleFocus;
      interactiveWrapperListeners.onBlur = handleBlur;
    }
  }
  const mergedPopperProps = _react.default.useMemo(() => {
    return _objectSpread({
      popperOptions: {
        modifiers: [{
          name: 'arrow',
          enabled: Boolean(arrowRef),
          options: {
            element: arrowRef,
            paddingRight: 10
          }
        }]
      }
    }, PopperProps || {});
  }, [arrowRef, PopperProps]);
  return /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, /*#__PURE__*/_react.default.cloneElement(children, childrenProps), /*#__PURE__*/_react.default.createElement(PopperComponent, (0, _extends2.default)({
    className: cx(classes.popper, {
      [classes.popperArrow]: arrow
    }),
    placement: placement,
    anchorEl: followCursor ? {
      getBoundingClientRect: () => new DOMRect(positionRef.current.x, positionRef.current.y, 0, 0)
    } : childNode,
    popperRef: popperRef
    //@ts-ignore TS2783 - mergedPopperProps can contains open
    ,
    open: childNode && open ? true : false,
    id: id,
    transition: true
  }, interactiveWrapperListeners, mergedPopperProps), _ref => {
    let {
      placement: placementInner,
      TransitionProps: TransitionPropsInner
    } = _ref;
    return /*#__PURE__*/_react.default.createElement(TransitionComponent, (0, _extends2.default)({
      timeout: theme.transitions.duration.shorter
    }, TransitionPropsInner, TransitionProps), /*#__PURE__*/_react.default.createElement("div", {
      className: cx(classes.tooltip, {
        [classes.touch]: ignoreNonTouchEvents.current,
        [classes.tooltipArrow]: arrow
      }, classes[getPlacementClassKey(placementInner)])
    }, title, arrow ? /*#__PURE__*/_react.default.createElement("span", {
      className: classes.arrow,
      ref: setArrowRef
    }) : null));
  }));
});
var _default = exports.default = Tooltip;